import { money, fmtDate } from "../format.js";
import { Badge } from "../ui.jsx";
import { letterheadOf } from "../../shared/letterhead.js";
import { FlowWordmark } from "./Logo.jsx";

/**
 * The rendered invoice / estimate "paper". Shared by the admin detail pages and
 * the client-facing page so both always show the same document.
 */
export default function DocumentView({ doc, company, client, kind = "invoice", compact, logo = null }) {
  const sym = doc.currency_symbol || "$";
  const isInvoice = kind === "invoice";
  const lines = doc.lines || [];
  const letterhead = letterheadOf(company);

  return (
    <div className={`doc ${compact ? "compact" : ""}`}>
      <div className="dh">
        <div style={{ maxWidth: 320 }}>
          {logo || <FlowWordmark height={26} tone="light" />}
          {/* Matches the clear space the PDF leaves under the mark (D.logoGap). */}
          <div style={{ marginTop: 20 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{letterhead.title}</div>
            <div className="tiny muted" style={{ lineHeight: 1.6 }}>
              {letterhead.lines.map((l, i) => (
                <div key={i}>{l}</div>
              ))}
              {letterhead.taxNumber && (
                <div style={{ fontWeight: 600, color: "var(--ink-2)", marginTop: 4 }}>{letterhead.taxNumber}</div>
              )}
            </div>
          </div>
        </div>
        <div className="right">
          <h1>{isInvoice ? "Invoice" : "Estimate"}</h1>
          <div className="sm dim num">{doc.number}</div>
          <div style={{ marginTop: 4 }}>
            <Badge status={doc.status} />
          </div>
        </div>
      </div>

      <div className="sep" style={{ margin: "26px 0" }} />

      <div className="row" style={{ alignItems: "flex-start", gap: 40 }}>
        <div style={{ flex: 1 }}>
          <div
            className="tiny muted"
            style={{ textTransform: "uppercase", letterSpacing: ".06em", fontWeight: 650 }}
          >
            Billed to
          </div>
          <div style={{ marginTop: 5 }}>
            <b>{client.name}</b>
            <div className="sm dim" style={{ lineHeight: 1.6 }}>
              {client.contact_name && <div>{client.contact_name}</div>}
              {String(client.address || "")
                .split("\n")
                .filter(Boolean)
                .map((l, i) => (
                  <div key={i}>{l}</div>
                ))}
              {client.email && <div>{client.email}</div>}
            </div>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "auto auto", gap: "5px 18px", fontSize: 13 }}>
          <span className="muted">Issued</span>
          <span className="right num">{fmtDate(doc.issue_date)}</span>
          <span className="muted">{isInvoice ? "Due" : "Expires"}</span>
          <span className="right num">{fmtDate(isInvoice ? doc.due_date : doc.expires_on)}</span>
          {doc.po_number ? (
            <>
              <span className="muted">PO number</span>
              <span className="right">{doc.po_number}</span>
            </>
          ) : null}
          {doc.project_name ? (
            <>
              <span className="muted">Project</span>
              <span className="right">{doc.project_name}</span>
            </>
          ) : null}
          {doc.title ? (
            <>
              <span className="muted">Scope</span>
              <span className="right">{doc.title}</span>
            </>
          ) : null}
        </div>
      </div>

      <table style={{ marginTop: 28 }}>
        <thead>
          <tr>
            <th>Description</th>
            <th className="right" style={{ width: 80 }}>
              Qty
            </th>
            <th className="right" style={{ width: 100 }}>
              Rate
            </th>
            <th className="right" style={{ width: 110 }}>
              Amount
            </th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.id}>
              <td>
                {l.description}
                {!l.taxable && <div className="tiny muted">Not subject to {doc.tax_label}</div>}
              </td>
              <td className="right num">{Number(l.qty) % 1 === 0 ? Number(l.qty) : Number(l.qty).toFixed(2)}</td>
              <td className="right num">{money(l.rate, sym)}</td>
              <td className="right num">{money(l.amount ?? l.qty * l.rate, sym)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="tot">
        <div className="tt">
          <div className="tr">
            <span className="muted">Subtotal</span>
            <span>{money(doc.subtotal, sym)}</span>
          </div>
          <div className="tr">
            <span className="muted">
              {doc.tax_label} ({Number(doc.tax_rate)}%)
            </span>
            <span>{money(doc.tax_amount, sym)}</span>
          </div>
          <div className="tr big">
            <span>Total</span>
            <span>{money(doc.total, sym)}</span>
          </div>
          {isInvoice && Number(doc.paid) > 0 && (
            <>
              <div className="tr">
                <span className="muted">Paid to date</span>
                <span style={{ color: "var(--ok)" }}>−{money(doc.paid, sym)}</span>
              </div>
              <div className="tr big">
                <span>Balance due</span>
                <span>{money(doc.balance, sym)}</span>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="sep" style={{ margin: "26px 0" }} />
      <div className="tiny muted" style={{ lineHeight: 1.7 }}>
        {doc.notes || company.footer_note}
        {isInvoice && (company.email || company.company_email) && (
          <div>Questions? {company.email || company.company_email}</div>
        )}
      </div>
    </div>
  );
}
